import type { ReactNode } from "react";

import { cn } from "~/app/lib/cn";
import { HOW_IT_WORKS } from "~/app/lib/partner";

/**
 * "Up and running in four steps", the for-students page's step band: a tinted
 * illustration with a numbered badge, a connector behind the row, and one line
 * of body each. The honest one is step three, where the student acts and the
 * institution does not.
 *
 * IT LIVES ON /pricing, NOT /overview (Thomas, Sep 21 2026). The steps answer
 * "what happens after we buy", which is a question a reader asks with the
 * price in front of them, and /overview had two sections in a row opening
 * "Built for...". Here it sits under "What is included", where the two columns
 * say what you get and this says how it starts.
 *
 * It is a component rather than markup on the page because the icons and the
 * step data travel with it; nothing else on /pricing draws a 24-box glyph.
 */

function Icon({ d }: { d: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {d}
    </svg>
  );
}

const STEP_ICON: readonly ReactNode[] = [
  <>
    <path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3" />
    <path d="M1 14h6M9 8h6M17 16h6" />
  </>,
  <>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </>,
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="m16 11 2 2 4-4" />
  </>,
  <>
    <path d="M12 20v-10M18 20V4M6 20v-4" />
  </>,
];

export function HowItWorks() {
  return (
    <ol className="eg-gs-grid">
      {HOW_IT_WORKS.map((step, index) => (
        <li className={cn("eg-gs-step", `tone-${index + 1}`)} key={step.n}>
          <div className="eg-gs-vis" aria-hidden="true">
            <span className="eg-gs-n">{step.n}</span>
            <Icon d={STEP_ICON[index]} />
          </div>
          <div className="eg-gs-body">
            <h3>{step.title}</h3>
            <p>{step.detail}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}
